// mapGeometryUtils.js — pure geometry helper functions extracted from MapEditorModal (ticket 01).

import { toLonLat, fromLonLat } from 'ol/proj';

// -- Helper: capture bbox of a pixel rect from an OL map ---------------------
export function bboxFromOlMapRect(map, pixelRect) {
    if (!map || !pixelRect) return null;
    const { left, top, width, height } = pixelRect;
    const sw = toLonLat(map.getCoordinateFromPixel([left, top + height]));
    const ne = toLonLat(map.getCoordinateFromPixel([left + width, top]));
    if (!sw || !ne) return null;
    return [[sw[0], sw[1]], [ne[0], ne[1]]];
}

// -- Helper: current viewport bbox as [minLon, minLat, maxLon, maxLat] -------
// Ticket 02: the "current viewport bbox" fed into the imagery coverage decision — distinct
// from `bboxFromOlMapRect` above (which captures the 16:9 vignette *output frame* for named
// Views), this is simply the OL view's full visible extent, transformed to lon/lat so it
// matches the `ImageryProvider` contract's bbox shape.
export function viewBboxLonLat(map) {
    const size = map.getSize();
    if (!size) return null;
    const [minX, minY, maxX, maxY] = map.getView().calculateExtent(size);
    const [minLon, minLat] = toLonLat([minX, minY]);
    const [maxLon, maxLat] = toLonLat([maxX, maxY]);
    return [minLon, minLat, maxLon, maxLat];
}

// -- Helper: current viewport bbox as [[swLng,swLat],[neLng,neLat]] ----------
// Ticket 06: same underlying OL extent as `viewBboxLonLat` above, just reshaped into the nested
// `[[sw],[ne]]` bbox convention used by `mapPmAdapter.js` (`featureBbox`/`layersInView`/
// `layersVisibleAtDtg`) instead of the flat 4-tuple the imagery `ImageryProvider` contract wants.
// Kept as a separate tiny helper rather than reshaping `viewBboxLonLat`'s return value at call
// sites so each call site reads naturally with the shape it actually needs.
export function viewportBboxNested(map) {
    const flat = viewBboxLonLat(map);
    if (!flat) return null;
    const [minLon, minLat, maxLon, maxLat] = flat;
    return [[minLon, minLat], [maxLon, maxLat]];
}

// -- Helper: pan the view to a lon/lat coordinate, keeping current zoom -----
// Manual-coordinate-entry (SymbolEditor "Position" fields): after a successful geometry edit,
// recenter the map on the symbol's new position rather than leaving the viewport pointed at
// wherever it used to be — mirrors useMapViews.js's flyToView animate() call.
export function flyToLonLat(map, [lon, lat], { duration = 600 } = {}) {
    if (!map) return;
    map.getView().animate({ center: fromLonLat([lon, lat]), duration });
}
